import { Button } from "primereact/button";

import { EmptyState, ErrorState, ForbiddenState, HubTable, StateMessage } from "@/ds";
import { HubTableCartao, HubTableDetailButton } from "@/shared/components/hub-table-cartao";
import type { FichasFinanceirasSortField } from "@/modules/financeiro-fichas";
import {
  COLUNA_CRIADO_EM,
  COLUNA_SOLICITANTE,
  COLUNA_STATUS_FEL,
  FICHAS_CARREGANDO_LABEL,
  FICHAS_EMPTY_DESCRIPTION,
  FICHAS_EMPTY_TITLE,
  FICHAS_LIST_TITLE,
  FICHAS_POR_PAGINA_LABEL,
  GERAR_FICHA_LABEL,
  FICHAS_TABELA_ALTURA_MAXIMA,
  GERAR_FICHA_LIST_LABEL,
  TABELA_LABEL,
} from "./fichas-tabela.constants";
import styles from "./fichas-tabela.module.scss";
import type { FichaColunaChave, FichaLinha, FichasTabelaViewModel } from "./types-fichas-tabela";

/**
 * Fichas da obra: carregando, sem permissão, erro, vazio ou o `HubTable` paginado e ordenado no
 * servidor (toda coluna, menos a lupa, ordena no cabeçalho). Clicar na linha ou na lupa abre a ficha.
 */
export function FichasTabelaView({
  estado,
  errorMessage,
  recarregar,
  podeGerarFicha,
  linhas,
  total,
  tabela,
  carregandoTabela,
  abrir,
  gerar,
}: FichasTabelaViewModel) {
  if (estado === "oculta") return null;

  if (estado === "carregando") {
    return (
      <div className={styles.corpo}>
        <StateMessage variant="loading" title={FICHAS_CARREGANDO_LABEL} testId="fichas-loading" />
      </div>
    );
  }

  if (estado === "proibido") {
    return (
      <div className={styles.corpo}>
        <ForbiddenState description={errorMessage ?? undefined} testId="fichas-error" />
      </div>
    );
  }

  if (estado === "erro") {
    return (
      <div className={styles.corpo}>
        <ErrorState
          message={errorMessage ?? undefined}
          onRetry={recarregar}
          testId="fichas-error"
        />
      </div>
    );
  }

  if (estado === "vazio") {
    return (
      <EmptyState.Root size="sm" testId="fichas-empty" className={styles.vazio}>
        <EmptyState.Title className={styles.vazioTitulo}>{FICHAS_EMPTY_TITLE}</EmptyState.Title>
        <EmptyState.Description className={styles.vazioDescricao}>
          {FICHAS_EMPTY_DESCRIPTION}
        </EmptyState.Description>
        {podeGerarFicha ? (
          <EmptyState.Action
            label={GERAR_FICHA_LABEL}
            text={false}
            // O Action é `secondary`; aqui vale o primário padrão, igual ao "Gerar Ficha" da lista.
            severity={undefined}
            // O Action fixa `small`; o Figma (12845:210974) pede a altura normal, 35.
            size={undefined}
            onClick={gerar}
            data-testid="fichas-gerar"
          />
        ) : null}
      </EmptyState.Root>
    );
  }

  return (
    <div className={styles.corpo}>
      <div className={styles.listaCabecalho}>
        <h3 className={styles.listaTitulo}>{FICHAS_LIST_TITLE}</h3>
        {podeGerarFicha ? (
          <Button
            type="button"
            label={GERAR_FICHA_LIST_LABEL}
            onClick={gerar}
            data-testid="fichas-gerar"
          />
        ) : null}
      </div>
      <HubTableCartao<FichaLinha, FichaColunaChave, FichasFinanceirasSortField>
        value={linhas}
        totalRecords={total}
        table={tabela}
        status={carregandoTabela ? "loading" : "success"}
        loadingLabel={FICHAS_CARREGANDO_LABEL}
        onRowClick={(linha) => abrir(linha.id)}
        dataTableProps={{
          scrollable: true,
          scrollHeight: FICHAS_TABELA_ALTURA_MAXIMA,
          "aria-label": TABELA_LABEL,
        }}
        rowsPerPageLabel={FICHAS_POR_PAGINA_LABEL}
        testId="fichas-table"
      >
        <HubTable.Column<FichaLinha, FichaColunaChave>
          field="criadoEm"
          sortField="criadoEm"
          header={COLUNA_CRIADO_EM}
          body={(linha) => <span className={styles.criadoEm}>{linha.criadoEm}</span>}
        />
        <HubTable.Column<FichaLinha, FichaColunaChave>
          field="statusFel"
          sortField="statusFel"
          header={COLUNA_STATUS_FEL}
          body={(linha) => <span data-testid={linha.statusTestId}>{linha.statusFel}</span>}
        />
        <HubTable.Column<FichaLinha, FichaColunaChave>
          field="solicitante"
          sortField="solicitante"
          header={COLUNA_SOLICITANTE}
          body={(linha) => linha.solicitante}
        />
        <HubTable.Column<FichaLinha, FichaColunaChave>
          field="acoes"
          header=""
          sortable={false}
          align="right"
          body={(linha) => (
            <HubTableDetailButton
              label={linha.rotuloAbrir}
              testId={linha.abrirTestId}
              onDetail={() => abrir(linha.id)}
            />
          )}
        />
      </HubTableCartao>
    </div>
  );
}
